import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Cinematic Preloader — staged reveal:
 *  1. Black + counter + diagonal stripes
 *  2. Kart silhouette zooms past
 *  3. Checkered flag wipes the screen out
 */
const FLASH_FRAMES = ["READY", "SET", "GO!"];

export const Preloader = () => {
  const { lang } = useLang();
  const [visible, setVisible] = useState(true);
  const [progress, setProgress] = useState(0);
  const [flash, setFlash] = useState("");
  // Detect low-power / mobile devices ONCE. We use this to strip the most
  // expensive paint work (infinite motion divs, curtains, radial glows) so
  // the preloader itself doesn't freeze cheap phones for 10+ seconds.
  const isMobile = useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(pointer: coarse)").matches || window.innerWidth < 1024;
  }, []);

  useEffect(() => {
    let pct = 0;
    const id = setInterval(() => {
      pct += Math.random() * 14 + 4;
      if (pct >= 100) {
        pct = 100;
        clearInterval(id);
      }
      setProgress(Math.round(pct));
    }, 95);
    return () => clearInterval(id);
  }, []);

  // 3-frame countdown flash when progress crosses 100, then dismiss.
  useEffect(() => {
    if (progress < 100) return;
    let i = 0;
    setFlash(FLASH_FRAMES[0]);
    const id = setInterval(() => {
      i += 1;
      if (i >= FLASH_FRAMES.length) {
        clearInterval(id);
        setTimeout(() => setVisible(false), 240);
        return;
      }
      setFlash(FLASH_FRAMES[i]);
    }, 320);
    return () => clearInterval(id);
  }, [progress]);

  const tip = useMemo(
    () =>
      lang === "en"
        ? [
            "Outdoor circuit · 960 m",
            "16 turns · 70 km/h top speed",
            "Open 364 days a year",
          ]
        : [
            "Открита писта · 960 м",
            "16 завоя · до 70 км/ч",
            "Отворено 364 дни в годината",
          ],
    [lang]
  );

  return (
    <AnimatePresence
      onExitComplete={() => {
        window.__preloaderDone = true;
        window.dispatchEvent(new Event("preloader:done"));
      }}
    >
      {visible && (
        <motion.div
          exit={{ y: "-101%" }}
          transition={{ duration: 0.95, ease: [0.85, 0, 0.15, 1] }}
          className="fixed inset-0 z-[100] bg-[#050505] overflow-hidden flex flex-col"
          data-testid="preloader"
        >
          {/* Diagonal racing stripes */}
          <div className="absolute inset-0 diagonal-stripes opacity-40 pointer-events-none" />

          {/* Top + bottom checkered flag bars */}
          <div className="absolute inset-x-0 top-0 h-6 md:h-8 checker opacity-30" />
          <div className="absolute inset-x-0 bottom-0 h-6 md:h-8 checker opacity-30" />

          {/* Side red bars (closing-curtain effect) */}
          <motion.div
            initial={{ x: "-100%" }}
            animate={{ x: "-100%" }}
            exit={{ x: "0%" }}
            transition={{ duration: 0.6, ease: [0.85, 0, 0.15, 1] }}
            className="absolute inset-y-0 left-0 w-1/2 bg-[#FF2D2D]"
          />
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: "100%" }}
            exit={{ x: "0%" }}
            transition={{ duration: 0.6, ease: [0.85, 0, 0.15, 1] }}
            className="absolute inset-y-0 right-0 w-1/2 bg-[#FF2D2D]"
          />

          {/* Speed-line streaks — desktop only (6 infinite framer-motion
              divs freeze low-end mobile GPUs for several seconds). */}
          {!isMobile && (
            <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
              {[15, 30, 45, 60, 75, 88].map((top, i) => (
                <motion.div
                  key={i}
                  initial={{ x: "-30%", opacity: 0 }}
                  animate={{ x: "130%", opacity: [0, 0.55, 0] }}
                  transition={{
                    duration: 0.9 + (i % 3) * 0.25,
                    delay: i * 0.12,
                    repeat: Infinity,
                    repeatDelay: 0.6,
                    ease: "easeOut",
                  }}
                  style={{ top: `${top}%` }}
                  className="absolute h-px w-40 md:w-56 bg-gradient-to-r from-transparent via-[#FF2D2D] to-transparent"
                />
              ))}
            </div>
          )}

          {/* Centre block — fades out when GO! flash starts */}
          <motion.div
            animate={{ opacity: flash ? 0 : 1, scale: flash ? 0.92 : 1 }}
            transition={{ duration: 0.25 }}
            className="relative flex-1 flex flex-col items-center justify-center px-6"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="font-mono-display text-[10px] md:text-xs tracking-[0.4em] text-[#FF2D2D] uppercase mb-6 md:mb-10"
            >
              {lang === "en" ? "Lights out · engine on" : "Светлини изключени · мотор включен"}
            </motion.div>

            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-6xl sm:text-7xl md:text-9xl font-bold uppercase tracking-tighter leading-none text-white"
              >
                Sofia<span className="text-[#FF2D2D]">.</span>
              </motion.h1>
            </div>
            <div className="overflow-hidden">
              <motion.h2
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="font-display text-6xl sm:text-7xl md:text-9xl font-bold uppercase tracking-tighter leading-none text-[#FF2D2D]"
              >
                Karting
              </motion.h2>
            </div>

            {/* Tips line */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.5 }}
              className="mt-6 md:mt-8 font-mono-display text-[10px] md:text-xs tracking-[0.25em] text-white/60 uppercase text-center"
            >
              {tip[Math.floor(progress / 34) % tip.length]}
            </motion.div>

            {/* Progress bar */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-8 md:mt-12 w-64 sm:w-80 md:w-[440px] flex items-center gap-4"
            >
              <div className="flex-1 h-[2px] bg-white/15 relative overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 bg-[#FF2D2D] transition-[width] duration-150 ease-out"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <div className="font-mono-display text-xs tracking-[0.2em] text-white tabular-nums w-10 text-right">
                {String(progress).padStart(3, "0")}
              </div>
            </motion.div>
          </motion.div>

          {/* Bottom info */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="relative px-6 md:px-12 pb-12 md:pb-14 flex flex-col md:flex-row items-center justify-between gap-3 font-mono-display text-[9px] md:text-[10px] tracking-[0.3em] text-white/40 uppercase"
          >
            <span>{lang === "en" ? "Krasna Polyana · Sofia" : "Красна поляна · София"}</span>
            <span>960m · 16 turns · 70 km/h</span>
          </motion.div>

          {/* Big countdown flash overlay */}
          <AnimatePresence>
            {flash && (
              <motion.div
                key={flash}
                initial={{ scale: 0.6, opacity: 0 }}
                animate={{ scale: 1.05, opacity: 1 }}
                exit={{ scale: 1.4, opacity: 0 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 z-10 flex items-center justify-center pointer-events-none"
              >
                <span className="font-display text-[28vw] md:text-[20vw] font-bold uppercase leading-none tracking-tighter text-[#FF2D2D] drop-shadow-[0_0_60px_rgba(255,45,45,0.6)]">
                  {flash}
                </span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default Preloader;
